<template>
  <div class="sidebar-group" :data-page-title="page && page.uniqueTitle">
    <div
      class="sidebar-item"
      :class="{
        active: activeUniqueTitle && activeUniqueTitle === page.uniqueTitle,
        'sidebar-child-item': depth > 0
      }"
    >
      <DragHandle
        allow-out
        :reorder-id="page && page.uniqueTitle"
        @move="movePage"
      />
      <button
        type="button"
        class="sidebar-open"
        :aria-current="activeUniqueTitle && activeUniqueTitle === page.uniqueTitle ? 'true' : undefined"
        @click="loadPage(page)"
      >
        <font-awesome-icon
          :icon="pageIsLoginProtected ? 'lock' : 'file'"
          class="sidebar-icon"
          :class="{ 'sidebar-child-icon': depth > 0 }"
        />
        <span class="sidebar-label">{{ (page && page.title) ? page.title : 'untitled' }}</span>
        <span v-if="hasPresentationRules" class="sidebar-presentation">presentation</span>
      </button>
    </div>
    <draggable
      :list="page.children"
      item-key="uniqueTitle"
      :group="{ name: 'page-tree', pull: true, put: true }"
      handle=".drag-handle"
      :filter="dragFilter"
      :preventOnFilter="false"
      :force-fallback="true"
      :fallback-on-body="true"
      :fallback-tolerance="4"
      fallback-class="page-drag-fallback"
      :data-page-parent="page && page.uniqueTitle"
      ghost-class="drag-ghost"
      :empty-insert-threshold="28"
      :move="canMovePage"
      class="sidebar-children page-nest-drop"
      :class="{ 'is-empty': !page.children.length }"
      :data-nest-label="nestLabel"
      @start="onDragStart"
      @unchoose="onDragClear"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <pageTree
          :page="element"
          :depth="depth + 1"
          :active-unique-title="activeUniqueTitle"
          :on-load-page="onLoadPage"
          :on-drag-start="onDragStart"
          :on-drag-clear="onDragClear"
          :on-drag-end="onDragEnd"
          :can-move-page="canMovePage"
          :drag-filter="dragFilter"
          :pages="pages"
          @reorder="forwardReorder"
        />
      </template>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import DragHandle from './DragHandle.vue'
import { pageDragFilter } from '../lib/page-record-link.js'
import { pageRequiresLogin } from '../../../shared/view-visibility.js'
import { hasPageOpenRules } from '../../../shared/page-open-rules.js'

export default {
  name: 'pageTree',
  components: { draggable, DragHandle },
  emits: ['reorder'],
  props: {
    page: Object,
    depth: {
      type: Number,
      default: 0
    },
    activeUniqueTitle: {
      type: String,
      default: null
    },
    onLoadPage: {
      type: Function,
      required: true
    },
    onDragStart: {
      type: Function,
      default () {
        return function () {}
      }
    },
    onDragEnd: {
      type: Function,
      default () {
        return function () {}
      }
    },
    onDragClear: {
      type: Function,
      default () {
        return function () {}
      }
    },
    canMovePage: {
      type: Function,
      default () {
        return function () { return true }
      }
    },
    dragFilter: {
      type: Function,
      default: pageDragFilter
    },
    pages: {
      type: Array,
      default () {
        return []
      }
    }
  },
  computed: {
    pageIsLoginProtected () {

      return pageRequiresLogin(this.page, this.pages)
    },
    hasPresentationRules () {

      return hasPageOpenRules(this.page)
    },
    nestLabel () {
      const title = this.page && this.page.title ? this.page.title : 'untitled'
      return `nest under ${title}`
    }
  },
  created () {
    if (this.page && !Array.isArray(this.page.children)) this.page.children = []
  },
  methods: {
    movePage (direction) {

      this.$emit('reorder', this.page, direction)
    },
    forwardReorder (page, direction) {

      this.$emit('reorder', page, direction)
    },
    loadPage (page) {
      this.onLoadPage(page)
    }
  }
}
</script>

<style scoped>
.sidebar-child-item {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 14px 5px 8px;
  cursor: pointer;
  color: #7a7a78;
  font-size: 0.82rem;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}
.sidebar-child-item:hover {
  background-color: #1a1a17;
  color: #c0c0be;
}
.sidebar-child-item.active {
  color: #e0e0de;
  border-left: 2px solid #9ad12b;
  padding-left: 6px;
}
.sidebar-child-icon {
  font-size: 0.65rem;
  color: #7a7a76;
  flex-shrink: 0;
}
.sidebar-presentation {
  flex-shrink: 0;
  font-size: 0.62rem;
  letter-spacing: 0.03em;
  color: #6ab0de;
}
.sidebar-children {
  background-color: #111110;
  padding-left: 14px;
}
.sidebar-children.is-empty {
  height: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background: transparent;
  pointer-events: none;
}
</style>
